import { gql } from 'apollo-angular';

export class ProductQueries {
    static PRODUCT_FRAGMENT = gql`
        fragment ProductInfo on Product {
            id
            enabled
            serviceCategory
            productContentType
            productLocaleScore
            productCategory
            productSubCategory
            title
            titleContentKey
            titleContentKeyValue
            descriptionContentKeyValue
            summaryKey
            summaryKeyValue
            validity {
                quantity
                unit
                formatted
            }
            proxyData
            providerId
            provider {
                id
                name
            }
            productReference
            price {
                amount
                currency
                formatted
            }
            cost {
                amount
                currency
                formatted
            }
            value {
                amount
                currency
                formatted
            }
            priceOverride {
                amount
                currency
                formatted
            }
            description
            descriptionContentKey
            countryId
            operator {
                id
                name
            }
            deliverables {
                amount
                unit
            }

            fixedPrices {
                amount
                currency
                formatted
            }
            priority
            routed
            subscriptionAvailability
            internalProductId
            internalProductId
            enabled
            note
            providerRemoved
        }
    `;

    static ROUTING_HISTORY_FRAGMENT = gql`
        ${this.PRODUCT_FRAGMENT}
        fragment RoutingHistoryInfo on ChangeHistory {
            id
            user {
                name
            }
            createdAt
            comment
        }
    `;

    static PRODUCT_ROUTING_FRAGMENT = gql`
        fragment ProductRoutingInfo on ProductRouting {
            id
            serviceCategory
            productCategory
            productSubCategory
            operatorId
            providerId
            operator {
                name
                id
                countryId
            }
            provider {
                name
                id
            }
        }
    `;

    static SEARCH_PRODUCTS_IDS = gql`
        query productSearchIds(
            $where: MarketplaceProductSearchFilterInput
            $pagination: PaginationInput
        ) {
            marketplaceProductSearch(where: $where, pagination: $pagination) {
                ids
                totalCount
            }
        }
    `;
    static PRODUCTS = gql`
        ${ProductQueries.PRODUCT_FRAGMENT}
        query productSearch(
            $where: MarketplaceProductSearchFilterInput
            $pagination: PaginationInput
        ) {
            marketplaceProductSearch(where: $where, pagination: $pagination) {
                totalCount
                items {
                    ...ProductInfo
                }
            }
        }
    `;
    static PRODUCTS_PROVIDER = gql`
        query productSearchProviders(
            $where: MarketplaceProductSearchFilterInput
            $pagination: PaginationInput
        ) {
            marketplaceProductSearch(where: $where, pagination: $pagination) {
                totalCount
                items {
                    providerId
                    operatorId
                    productCategory
                    productSubCategory
                    provider {
                        name
                        id
                    }
                    operator {
                        id
                        name
                    }
                }
            }
        }
    `;
    static PRODUCTHISTORY = gql`
        query getProductHistory($productId: ID!, $pagination: PaginationInput) {
            marketplaceProductHistorySearch(
                input: { productId: $productId }
                pagination: $pagination
            ) {
                totalCount
                items {
                    providerId
                    title
                    description
                    price {
                        amount
                        currency
                        formatted
                    }
                    cost {
                        amount
                        currency
                        formatted
                    }
                    overridePriceAmount
                    operatorId
                    countryId
                    enabled
                    providerRemoved
                    fixedPrices {
                        amount
                        currency
                        formatted
                    }
                    value {
                        amount
                        currency
                        formatted
                    }
                    validity {
                        quantity
                        unit
                    }
                    validFrom
                    note
                }
            }
        }
    `;
    static ROUTING = gql`
        query routingSearch(
            $where: MarketplaceProductRoutingSearchFilterInput
            $pagination: PaginationInput
        ) {
            marketplaceProductRoutingSearch(
                where: $where
                pagination: $pagination
            ) {
                totalCount
                items {
                    id
                    serviceCategory
                    productCategory
                    productSubCategory
                    operatorId
                    providerId
                    operator {
                        name
                        id
                        countryId
                    }
                    provider {
                        name
                        id
                    }
                }
            }
        }
    `;

    static DELETEROUTE = gql`
        mutation deleteRoute($input: MarketplaceProductRoutingDeleteInput!) {
            marketplaceProductRoutingDelete(input: $input) {
                id
            }
        }
    `;

    static UPDATEROUTE = gql`
        mutation updateRoute($input: MarketplaceProductRoutingUpdateInput!) {
            marketplaceProductRoutingUpdate(input: $input) {
                successfulIds
            }
        }
    `;

    static ADDROUTE = gql`
        mutation addRoute($input: MarketplaceProductRoutingAddInput!) {
            marketplaceProductRoutingAdd(input: $input) {
                id
                # productRouting {
                #     id
                #     serviceCategory
                #     productCategory
                #     productSubCategory
                #     operatorId
                #     providerId
                #     operator {
                #         name
                #         id
                #         countryId
                #     }
                #     provider {
                #         name
                #         id
                #     }
                # }
            }
        }
    `;

    static CHANGE_HISTORY = gql`
        query getRoutingChangeHistory {
            configurationChangeHistorySearch(
                where: { changeType: { in: "MarketplaceRouting" } }
                pagination: { take: 5 }
            ) {
                items {
                    id
                    user {
                        name
                    }
                    createdAt
                    comment
                }
            }
        }
    `;

    static SAVE_PRODUCT = gql`
        mutation updateProduct($input: MarketplaceProductUpdateInput!) {
            marketplaceProductUpdate(input: $input) {
                successfulIds
                failedUpdates {
                    id
                    message
                }
                products {
                    id
                    enabled
                    titleContentKey
                    descriptionContentKey
                    productLocaleScore
                    productContentType
                    priceOverride {
                        amount
                        currency
                        formatted
                    }
                    fixedPrices {
                        amount
                        currency
                        formatted
                    }
                    priority
                    note
                }
            }
        }
    `;
    static SAVE_PLAN = gql`
        mutation planCreate($input: MarketplacePlanCreateInput!) {
            marketplacePlanCreate(input: $input) {
                id
            }
        }
    `;

    static RELOAD_PRODUCTS = gql`
        mutation reloadProducts($input: MarketplaceProductReloadInput!) {
            marketplaceProductReload(input: $input) {
                providerId
            }
        }
    `;

    static PRODUCTS_IDS = gql`
        query getProductsIds($ids: [ID!]!) {
            marketplaceInternalProductSearch(
                where: { internalProductIds: { in: $ids } }
            ) {
                totalCount
                ids
            }
            marketplaceProductSearch(where: { productIds: { in: $ids } }) {
                ids
                totalCount
            }
        }
    `;
    static PRODUCT_SEARCH = gql`
        query searchProductsAndInternalProducts(
            $productFilter: MarketplaceProductSearchFilterInput
            $internalProductsFilter: MarketplaceInternalProductSearchFilterInput
            $includeProducts: Boolean! = true
            $includeInternal: Boolean! = true
        ) {
            marketplaceInternalProductSearch(where: $internalProductsFilter)
                @include(if: $includeInternal) {
                totalCount
                ids
            }
            marketplaceProductSearch(where: $productFilter)
                @include(if: $includeProducts) {
                ids
                totalCount
            }
        }
    `;

    static PRODUCTDELIVERABLES = gql`
        query productDeliverablesSearch(
            $where: MarketplaceProductSearchFilterInput
        ) {
            marketplaceProductSearch(where: $where) {
                totalCount
                items {
                    id
                    deliverables {
                        deliverableType
                        unitType
                        amount
                        unit
                        summaryKey
                    }
                }
            }
        }
    `;

    static SAVEPRODUCTDELIVERABLE = gql`
        mutation productDeliverableCreate(
            $input: MarketplaceProductDeliverableCreateInput!
        ) {
            marketplaceProductDeliverableCreate(input: $input) {
                success
            }
        }
    `;

    static PRODUCTAIDELIVERABLES = gql`
        query productAiDeliverableSearch(
            $where: MarketplaceProductSearchFilterInput
        ) {
            marketplaceProductSearch(where: $where) {
                totalCount
                items {
                    id
                    aiDeliverables {
                        amount
                        unit
                        summary
                        deliverableId
                    }
                }
            }
        }
    `;

    static DELETEDELEVERABLE = gql`
        mutation deleteAiDeliverable(
            $input: MarketplaceProductAiDeliverableDeleteInput!
        ) {
            marketplaceProductAiDeliverableDelete(input: $input) {
                productId
                deliverableId
            }
        }
    `;

    static UPDATEDELEVERABLE = gql`
        mutation updateAiDeliverable(
            $input: MarketplaceProductAiDeliverableUpdateInput!
        ) {
            marketplaceProductAiDeliverableUpdate(input: $input) {
                productId
                deliverableId
            }
        }
    `;

    static PRODUCTLOCALES = gql`
        query getProductLocales($where: MarketplaceProductSearchFilterInput) {
            marketplaceProductSearch(where: $where) {
                totalCount
                items {
                    id
                    locales {
                        description
                        title
                        locale
                    }
                }
            }
        }
    `;

    static UPDATELOCALES = gql`
        mutation updateProductAiLocale(
            $input: MarketplaceProductAiLocaleUpdateInput!
        ) {
            marketplaceProductAiLocaleUpdate(input: $input) {
                productId
            }
        }
    `;
}
